<template>
  <v-card flat class="px-3">
    <v-col
      cols="12"
      class="body-bold-smaller py-0"
    >
      <a type="primary" size="xsmall" @click="handleBack">
        <v-icon size="12" class="backIcon">mdi-arrow-left</v-icon>
        {{ $t('message.actions.return') }}
      </a>
    </v-col>

    <v-col class="pt-1 py-0">
      <span class="body-bold-medium">{{
        $t('message.ai.abstract.ebook')
      }}</span>
    </v-col>

    <v-col cols="12" class="py-0">
      <span class="body-regular-smaller textType6--text pageDescription">
        {{ $t('message.ai.abstract.ebookDescription') }}
        <a target="_blank" type="secondary" :href="ebookArticle">
          <span>
            {{ $t('message.embedModal.knowMore') }}
          </span>
          <v-icon size="12">mdi-open-in-new</v-icon>
        </a>
      </span>
    </v-col>

    <div :class="{ 'd-none': isLoading }" style="width: 100%">
      <v-row no-gutters class="py-3 pt-0 container__download-ebook">
        <v-col cols="12" class="pl-3 pt-6">
          <span class="body-bold-medium"
            >Panda AI - {{ $t('message.ai.abstract.ebook') }}</span
          >
        </v-col>

        <v-col class="pa-0 pl-3 py-4 pb-3" cols="6">
          <p class="mb-0 body-regular-small">
            {{ $t('message.ai.abstract.activeEbook') }}
          </p>
          <span class="body-regular-smaller textType6--text"
            >{{ $t('message.ai.abstract.enableEbook') }}
          </span>
        </v-col>

        <v-col cols="6" class="d-flex align-center justify-end">
          <v-switch
            v-model="isEnabledInPlayer"
            @click="toggleActivation"
            :disabled="preventToggleQuickly"
            :loading="preventToggleQuickly"
            class="align-center mt-0 pt-0"
            compact
            hide-details
          ></v-switch>
        </v-col>

        <v-col v-if="isEnabledInPlayer" cols="12" class="mt-4 mb-10 px-3">
          <v-divider />
        </v-col>
        <template v-if="isEnabledInPlayer">
          <v-col cols="12">
            <span class="body-bold-medium pl-3">{{
              $t('message.ai.abstract.ebookContent')
            }}</span>
          </v-col>

          <v-col cols="12" class="mt-7 d-flex justify-space-between mb-3">
            <div>
              <span class="body-regular-small ml-3">{{
                $t('message.ai.abstract.viewEbook')
              }}</span>
            </div>
            <div>
              <v-tooltip top v-model="editTooltip">
                <template v-slot:activator="{ on, attrs }">
                  <span
                    class="ebookBtn"
                    @click="handleEditModalEditMode"
                    v-on="on"
                    v-bind="attrs"
                  >
                    <span @click="handleEditModalEditMode">
                      <v-icon class="iconBtn" size="16">panda-edit</v-icon>
                    </span>
                  </span>
                </template>
                <span>{{ $t('message.actions.edit') }} </span>
              </v-tooltip>

              <v-tooltip top v-model="downloadTooltip">
                <template v-slot:activator="{ on, attrs }">
                  <span
                    class="ebookBtn mx-4"
                    @click="handleDownloadEbook"
                    v-on="on"
                    v-bind="attrs"
                  >
                    <span>
                      <v-icon class="iconBtn" size="16">panda-download</v-icon>
                    </span>
                  </span>
                </template>
                <span>{{ $t('message.download') }} </span>
              </v-tooltip>

              <base-btn
                size="small"
                outlined
                type="tertiary"
                @click="handleEditModal"
              >
                {{ $t('message.actions.open') }}
              </base-btn>
            </div>
          </v-col>
        </template>
      </v-row>
    </div>

    <VideoPlayerEbookModal />
  </v-card>
</template>

<script>
import jsPDF from 'jspdf'
import { mapState, mapActions, mapMutations } from 'vuex'
import playerApi from '@/services/playerApi.js'
import videosApi from '@/services/videosApi.js'
import { CONFIG_URL } from '@/config/envs.js'
import aiworkflowApi from '@/services/aiworkflowApi'
import VideoPlayerEbookModal from './VideoPlayerEbookModal.vue'
import i18n from '@/translations/lang'

export default {
  name: 'VideoPlayerEbook',
  components: { VideoPlayerEbookModal },
  props: {
    handleBack: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      isLoading: true,
      formattedText: '',
      isEnabledInPlayer: false,
      preventToggleQuickly: false,
      downloadTooltip: false,
      editTooltip: false,
      videoConfig: {},
      editableObjects: [],
      originalTextCopy: '',
      copyText: '',
      beingEditedObject: null,
      ebookLoading: false,
    }
  },
  created() {
    this.checkEnabledItems()
  },
  mounted() {
    this.handleData()
    this.$root.$on('updateEbookData', (abstract) => {
      this.setAbstract(abstract)
    })
    this.$root.$on('handleDownloadEbook', () => {
      this.handleDownloadEbook()
    })
  },
  beforeDestroy() {
    this.$root.$off('updateEbookData')
    this.$root.$off('handleDownloadEbook')
  },
  computed: {
    ...mapState({
      video: (state) => state.video.video,
      hasData: (state) => state.video.aiWorkFlow?.data,
    }),
    ebookArticle() {
      if (this.locale === 'en') {
        return 'https://help.pandavideo.com/en-us/article/panda-ai-ebook-xu47wi/'
      }
      if (this.locale === 'es') {
        return 'https://help.pandavideo.com/es/article/panda-ai-ebook-15yzp95/'
      }
      return 'https://help.pandavideo.com/pt-br/article/panda-ia-ebook-21wysx/'
    },
    locale() {
      return this.$i18next.resolvedLanguage
    },
    hasChanged() {
      return this.originalTextCopy === this.copyText
    },
  },
  methods: {
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    ...mapMutations('video', ['UPDATE_VIDEO_CONFIG']),
    ...mapActions('video', ['handleFetchAiWorkFlowData']),
    handleEditModal() {
      this.$root.$emit('openVideoEbookModal', { ebook: this.originalTextCopy })
    },
    handleEditModalEditMode() {
      this.$root.$emit('openVideoEbookModal', {
        ebook: this.originalTextCopy,
        editMode: true,
      })
    },
    handleKnowMore() {
      const link = this.getEbookLink()
      window.open(link, '_blank')
    },
    convertMarkdownToPDF(markdownText) {
      const doc = new jsPDF()
      let yPos = 10

      const addLineToPDF = (text, size, isBulletPoint = false) => {
        doc.setFontSize(size)

        const lines = doc.splitTextToSize(text, 180)
        lines.forEach((line) => {
          if (yPos > 280) {
            doc.addPage()
            yPos = 10
          }

          if (isBulletPoint) {
            doc.text(`• ${line}`, 10, yPos)
          } else {
            doc.text(line, 10, yPos)
          }
          yPos += size + 2
        })
      }

      const lines = markdownText.split('\n')
      lines.forEach((line) => {
        if (line.startsWith('# ')) {
          addLineToPDF(line.substring(2), 22)
        } else if (line.startsWith('## ')) {
          addLineToPDF(line.substring(3), 18)
        } else if (line.startsWith('### ')) {
          addLineToPDF(line.substring(4), 14)
        } else if (line.startsWith('- ')) {
          addLineToPDF(line.substring(2), 12, true)
        } else {
          addLineToPDF(line, 12)
        }
      })

      doc.save(`${this.video.id}_ebook.pdf`)
    },
    handleDownloadEbook() {
      if (this.video.config.ai.pdf_generated) {
        const url = `${CONFIG_URL}/${this.video.pullzone_name}/${this.video.video_external_id}/abstract.pdf`
        fetch(url)
          .then((response) => response.blob())
          .then((blob) => {
            const file = window.URL.createObjectURL(blob)
            const tempDownloadTag = document.createElement('a')
            tempDownloadTag.href = file
            tempDownloadTag.download = 'abstract.pdf'
            document.body.appendChild(tempDownloadTag)
            tempDownloadTag.click()
            tempDownloadTag.remove()
          })
          .catch((err) => {
            console.error(err)
          })
      } else {
        const { abstract } = this.hasData
        this.convertMarkdownToPDF(abstract)
      }
    },
    async handleData() {
      if (this.hasData) {
        const { abstract } = this.hasData
        this.setAbstract(abstract)
      } else {
        let { pullzone_name, video_external_id } = this.video

        try {
          const { abstract } = await this.handleFetchAiWorkFlowData({
            pullzone_name,
            video_external_id,
          })
          this.setAbstract(abstract)
        } catch (error) {
          console.error(error)
        }
      }
    },
    setAbstract(abstract) {
      this.originalTextCopy = abstract.slice()
      this.copyText = abstract.slice()
      this.formatText(abstract)
      let { pullzone_name, video_external_id } = this.video
      this.handleFetchAiWorkFlowData({
        pullzone_name,
        video_external_id,
      })
    },
    async updateEbook() {
      this.ebookLoading = true
      const videoId = this.$route.params.id
      const newTextValue = this.copyText.slice()
      const aiData = {
        video_id: videoId,
        abstract: newTextValue,
      }
      try {
        let res = await aiworkflowApi.updateAiWorkFlowData(aiData)
        this.setAbstract(res.data.abstract)
        this.ebookLoading = false
      } catch (e) {
        this.ebookLoading = false
        console.error('e', e)
      }
    },
    formatText(originalText) {
      const lines = originalText.split('\n')
      let result = ''
      const objects = []

      for (const line of lines) {
        if (line.startsWith('#')) {
          const level = line.lastIndexOf('#') + 2
          const title = line.slice(level).trim()
          const stringText = `<h${level}>${title}</h${level}>`
          result += stringText
          objects.push({
            editableText: title,
            originalText: title,
            line: stringText,
          })
        } else {
          const formattedLine = line.replace(
            /\*\*(.*?)\*\*/g,
            '<strong>$1</strong>'
          )
          const stringText2 = `<span class="textType2--text text-body-1">${formattedLine}</span>`
          result += stringText2
          objects.push({
            editableText: formattedLine,
            originalText: formattedLine,
            line: stringText2,
          })
        }
      }
      this.editableObjects = objects
      this.formattedText = result
    },
    handleEditText(editObject) {
      this.beingEditedObject = editObject
    },
    handleCancelEditText() {
      this.beingEditedObject = null
    },
    undoChanges() {
      this.copyText = this.originalTextCopy.slice()
      this.formatText(this.originalTextCopy)
    },
    editLineText(editObject) {
      // editObject.editableText = edit
      this.copyText = this.copyText.replace(
        editObject.originalText,
        editObject.editableText
      )
      this.formatText(this.copyText)
      this.beingEditedObject = null
    },
    async toggleActivation() {
      this.preventToggleQuickly = true
      this.videoConfig['abstract'] = this.isEnabledInPlayer
      await playerApi.updateAiVideoConfig(this.video.id, this.videoConfig)
      this.UPDATE_GLOBAL_SNACKBAR({
        open: true,
        text: i18n.t('message.ai.abstract.ebookStatus'),
        type: 'success',
      })
      this.preventToggleQuickly = false
    },
    async checkEnabledItems() {
      try {
        const {
          data: { config },
        } = await videosApi.getAVideo(this.video.id)
        this.UPDATE_VIDEO_CONFIG(config)
        this.isEnabledInPlayer = ((config || {}).ai || {}).abstract || false
        this.videoConfig = (config || {}).ai || {}
        this.isLoading = false
      } catch (error) {
        console.log(error)
        this.isLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.scroll-container {
  max-height: 500px;
  overflow-y: auto;
  overflow-x: hidden;
}

.iconBtn {
  cursor: pointer;
}

.ebookBtn {
  padding: 3px 4px 4px 4px;
}

.ebookBtn:hover {
  border-radius: var(--spacing-02, 4px);
  background: varFigma(neutral-95-lightest);
}

.container__download-ebook {
  margin-top: 3px !important;
  padding-bottom: 19px !important;
}

.container__download-ebook {
  margin-top: 3px !important;
  padding-bottom: 19px !important;
}
</style>
